/**
 * Unit 2.IMPL.D — DocumentList loading skeleton.
 *
 * Bead: insurescope-zkolv.1.2.3.4.
 *
 * Renders 8 placeholder TableRows during the first-load query state
 * (`isFetching && !data`). Lives in its own file so the consumer's body
 * branch dispatch is a one-line `<DocumentListSkeleton colCount={N} />`
 * instead of an inline 8-row map.
 *
 * IMPORTANT — the data-testid contract
 * ------------------------------------
 * Unit 2.RED #2 counts skeleton rows via:
 *   `container.querySelectorAll('[data-slot="skeleton"], [data-testid="document-list-skeleton-row"]')`
 * and asserts `length === 8`. The shadcn `<Skeleton>` primitive emits
 * `data-slot="skeleton"`, so using it inside each cell would over-count to
 * `8 * colCount`. Instead, each `<TableRow>` carries the `data-testid` marker
 * AND inner cells use plain `<div>` placeholders (no shadcn Skeleton). The
 * union selector matches exactly the 8 rows.
 */
import { cn } from "@/lib/utils";
import { TableCell, TableRow } from "@/components/ui/table";

const SKELETON_ROW_COUNT = 8;

interface DocumentListSkeletonProps {
    /**
     * Number of cells per row. Must equal the rendered column count so the
     * skeleton spans the full table width (no orphan columns on the right
     * when the actions / checkbox columns are present).
     */
    colCount: number;
}

export function DocumentListSkeleton({ colCount }: DocumentListSkeletonProps) {
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
                <TableRow
                    key={`document-list-skeleton-${rowIdx}`}
                    data-testid="document-list-skeleton-row"
                >
                    {Array.from({ length: colCount }).map((_, colIdx) => (
                        <TableCell
                            key={`document-list-skeleton-${rowIdx}-${colIdx}`}
                        >
                            <div
                                className={cn(
                                    "bg-muted/60 h-4 animate-pulse rounded",
                                    // First cell (name) gets a wider
                                    // placeholder so the visual rhythm matches
                                    // the populated table.
                                    colIdx === 0 ? "w-40" : "w-20"
                                )}
                            />
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </>
    );
}
